import { setDefaultOptions } from 'date-fns';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import {
  i18n,
  loadLanguageAsync,
} from '@apps/main/modules/localization/localizationSetup';
import router from '@apps/main/modules/router/routerSetup';

import {
  assignExperimentValue,
  getExperiment,
  initializeStatsig,
  trackPerformance,
  trackPerformanceAppInitiated,
  trackPerformancePageLoaded,
} from '@libs/feature-toggle';
import { getLocale } from '@libs/sembo-dates';
import { decodeHTML, measureInitializationPerformance } from '@libs/utils';

import App from './App.vue';
import { languageLocale } from './modules/localization/localeUtils';
import { createLogger } from './modules/plugins/logger';
import './styles/main.scss';

if (decodeHTML(location.href) !== location.href) {
  location.href = decodeHTML(location.href) ?? location.href;
}

const initializationPerformanceMeasurement = measureInitializationPerformance();
trackPerformance();

const app = createApp(App);

const pinia = createPinia();
app.use(pinia);

(async function (): Promise<void> {
  await initializeStatsig();

  initializationPerformanceMeasurement.then(
    ({ timeToAppInitiatedMs, timeToPageLoadedMs }) => {
      trackPerformancePageLoaded(timeToPageLoadedMs);
      trackPerformanceAppInitiated(timeToAppInitiatedMs);
    }
  );

  const isHotelDetailsRoomCardsGridLayoutExperiment = getExperiment(
    'hotel_details_room_cards_grid_layout'
  ).get('hotel_details_room_cards_grid_layout', false);
  assignExperimentValue(
    'hotel_details_room_cards_grid_layout',
    isHotelDetailsRoomCardsGridLayoutExperiment
  );

  const isCalendarWithoutPricesInCarTripExperiment = getExperiment(
    'calendar_without_prices_in_car_trip'
  ).get('should_show_calendar_without_prices_in_car_trip', false);
  assignExperimentValue(
    'calendar_without_prices_in_car_trip',
    isCalendarWithoutPricesInCarTripExperiment
  );

  const isCarouselLastCardCopyUpdateExperiment = getExperiment(
    'carousel_last_card_copy_update'
  ).get('should_show_carousel_last_card_copy_update', false);
  assignExperimentValue(
    'carousel_last_card_copy_update',
    isCarouselLastCardCopyUpdateExperiment
  );

  const isNewBookTypeButtonExperiment = getExperiment(
    'new_book_type_button'
  ).get('should_show_new_book_type_button', false);
  assignExperimentValue('new_book_type_button', isNewBookTypeButtonExperiment);

  const isLowPriceCalendarButtonExperiment = getExperiment(
    'low_price_calendar_button'
  ).get('should_see_low_price_calendar_button', false);
  assignExperimentValue(
    'low_price_calendar_button',
    isLowPriceCalendarButtonExperiment
  );

  const isSeeAllHotelsButtonExperiment = getExperiment(
    'see_all_hotel_button'
  ).get('should_see_button', false);
  assignExperimentValue('see_all_hotel_button', isSeeAllHotelsButtonExperiment);
})();

const logger = createLogger();
app.use(logger);

app.use(router);
app.use(i18n);

app.mount('#app');

setDefaultOptions({
  locale: getLocale(languageLocale),
});

loadLanguageAsync(languageLocale);
